import React, { useEffect, useState, useCallback } from 'react';
import styles from './ManageUsersPage.module.css';
import MainLayout from '../../layouts/MainLayout';
import apiClient from '../../api/apiClient';
import Banner from '../../components/Banner';
import ConfirmDialog from '../../components/Dialog';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const ManageUsersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [newRole, setNewRole] = useState('standard');
  const [newPassword, setNewPassword] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    userId: null,
    username: '',
  });

  const client = apiClient('iam');

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await client.get('/iam/manage');
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [client]);

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchUsers();
    }
  }, [fetchUsers, user]);

  // If user is not loaded yet, don't render anything
  if (!user) {
    return (
      <MainLayout>
        <div className={styles.container}></div>
      </MainLayout>
    );
  }

  // If user is not admin, show a message and do not render the page
  if (user.role !== 'admin') {
    return (
      <MainLayout>
        <div className={styles.container}>
          <Banner
            open={true}
            message="You do not have permission to view this page. Only admin users can manage users."
            severity="error"
            onClose={() => navigate('/surveys/upload')}
            autoHideDuration={1000000}
          />
        </div>
      </MainLayout>
    );
  }

  const handleRoleChange = async (id, role) => {
    try {
      await client.put(`/iam/${id}/role`, { role });
      fetchUsers();
      setSnackbar({
        open: true,
        message: 'Role updated successfully.',
        severity: 'success',
      });
    } catch (error) {
      if (
        error.response &&
        error.response.status === 400 &&
        error.response.data?.detail === 'Cannot remove the last admin role'
      ) {
        setSnackbar({
          open: true,
          message:
            'Cannot remove the last admin role. There must always be at least one admin user.',
          severity: 'error',
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Error updating role.',
          severity: 'error',
        });
      }
      console.error('Error updating role:', error);
    }
  };

  const generatePassword = () => {
    const chars =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    return Array.from(
      { length: 8 },
      () => chars[Math.floor(Math.random() * chars.length)]
    ).join('');
  };

  const handleCreateUser = async () => {
    if (!newUsername.trim()) return;

    const password = generatePassword();
    try {
      const res = await client.post('/iam/register', {
        username: newUsername,
        password,
        role: newRole,
      });

      if (res.status === 201 || res.status === 200) {
        setNewUsername('');
        setNewPassword(password);
        fetchUsers();
        setSnackbar({
          open: true,
          message: 'User created successfully.',
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: 'User creation failed.',
          severity: 'error',
        });
      }
    } catch (error) {
      if (
        error.response &&
        error.response.status === 400 &&
        error.response.data?.detail === 'Username already registered'
      ) {
        setSnackbar({
          open: true,
          message: 'A user with this name already exists.',
          severity: 'error',
        });
      } else {
        setSnackbar({
          open: true,
          message: 'User creation failed.',
          severity: 'error',
        });
      }
      console.error(error);
    }
  };

  const handleDeleteUser = async (id) => {
    const user = users.find((u) => u.id === id);
    setDeleteDialog({
      open: true,
      userId: id,
      username: user ? user.username : '',
    });
  };

  const confirmDeleteUser = async () => {
    try {
      await client.delete(`/iam/${deleteDialog.userId}`);
      fetchUsers();
      setSnackbar({
        open: true,
        message: 'User deleted successfully.',
        severity: 'success',
      });
    } catch (error) {
      if (
        error.response &&
        error.response.status === 400 &&
        error.response.data?.detail === 'Cannot delete the last admin user'
      ) {
        setSnackbar({
          open: true,
          message:
            'Cannot delete the last admin user. There must always be at least one admin user.',
          severity: 'error',
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Error deleting user.',
          severity: 'error',
        });
      }
      console.error('Error deleting user:', error);
    } finally {
      setDeleteDialog({ open: false, userId: null, username: '' });
    }
  };

  const cancelDeleteUser = () => {
    setDeleteDialog({ open: false, userId: null, username: '' });
  };

  const copyToClipboard = () => {
    if (newPassword) {
      navigator.clipboard.writeText(newPassword);
      setSnackbar({
        open: true,
        message: 'Password copied to clipboard',
        severity: 'info',
      });
    }
  };

  return (
    <MainLayout>
      <div className={styles.container}>
        <h2 className={styles.title}>Manage Users</h2>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Username</th>
              <th>Role</th>
              <th>Delete️</th>
            </tr>
          </thead>
          <tbody>
            {users
              .slice()
              .sort((a, b) => a.username.localeCompare(b.username))
              .map((u) => (
                <tr key={u.id}>
                  <td>{u.username}</td>
                  <td>
                    <select
                      value={u.role}
                      onChange={(e) => handleRoleChange(u.id, e.target.value)}
                    >
                      <option value="standard">standard</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td>
                    <button
                      className={styles.deleteButton}
                      onClick={() => handleDeleteUser(u.id)}
                    >
                      ❌
                    </button>
                  </td>
                </tr>
              ))}
            <tr>
              <td>
                <input
                  type="text"
                  placeholder="New username"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                />
              </td>
              <td>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                >
                  <option value="standard">standard</option>
                  <option value="admin">admin</option>
                </select>
              </td>
              <td>
                <button
                  className={styles.createButton}
                  onClick={handleCreateUser}
                >
                  Create
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        {newPassword && (
          <div className={styles.passwordNotice}>
            New user created with password:{' '}
            <code className={styles.generatedPassword}>{newPassword}</code>
            <button className={styles.copyButton} onClick={copyToClipboard}>
              Copy to Clipboard
            </button>
          </div>
        )}
        <Banner
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        />
        <ConfirmDialog
          open={deleteDialog.open}
          title="Delete User"
          content={
            <>
              Are you sure you want to delete user{' '}
              <b>{deleteDialog.username}</b>?
            </>
          }
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={confirmDeleteUser}
          onCancel={cancelDeleteUser}
        />
      </div>
    </MainLayout>
  );
};

export default ManageUsersPage;
